import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Moon, Percent, Sun } from 'lucide-react';
import { INDIAN_STATES, resolveColour, type CategoryColour, type OrgSettings } from '@fint/validation';
import { api, ApiError } from '@/lib/api';
import { VersionCard } from './VersionCard';
import { ToursCard } from '@/features/tour/ToursCard';
import { useActiveOrg, useAuthStore } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { useThemeStore, type ThemePref } from '@/stores/theme';
import { PageHeader } from '@/app/DashboardLayout';
import { cn } from '@/lib/cn';
import {
  Badge, Button, Card, CardContent, CardHeader, CardTitle, ColourDot, ColourPicker, ErrorState, Input, SkeletonTable,
} from '@/features/common/ui';
import { PeriodLocksCard } from './PeriodLocksCard';
import { LutsCard } from './LutsCard';
import { ApiKeysCard } from './ApiKeysCard';
import { ConnectionsCard } from './ConnectionsCard';
import { LetterheadCard } from './LetterheadCard';
import { InstallCard } from '@/features/install/InstallCard';
import { OrganisationSettingsCard } from './OrganisationPage';

interface Tax { id: string; name: string; percentage: string; isActive: boolean }

/**
 * Settings, with the GST switch as the centrepiece.
 *
 * Off by default, so the app works anywhere. Turning it on is what unlocks
 * GSTIN, place of supply, HSN/SAC and the CGST/SGST vs IGST split - and it
 * seeds the Indian rates at that moment rather than at signup, so an org that
 * never enables GST never carries them in its pickers.
 */
export function SettingsPage() {
  const org = useActiveOrg();
  const user = useAuthStore((s) => s.user);
  const perms = usePermissions();
  const queryClient = useQueryClient();
  const themePref = useThemeStore((s) => s.pref);
  const setThemePref = useThemeStore((s) => s.setPref);

  const settings = useQuery({
    queryKey: ['settings', org?.id],
    queryFn: () => api.get<OrgSettings>('/settings'),
    enabled: !!org?.id,
  });
  const taxes = useQuery({
    queryKey: ['taxes', org?.id],
    queryFn: () => api.get<{ items: Tax[] }>('/taxes'),
    enabled: !!org?.id,
  });

  const [gstEnabled, setGstEnabled] = useState(false);
  const [stateCode, setStateCode] = useState('');
  const [gstin, setGstin] = useState('');

  // Seed the form from the server once it arrives, without clobbering edits in
  // progress on a background refetch.
  useEffect(() => {
    if (!settings.data) return;
    setGstEnabled(settings.data.tax?.regime === 'gst');
    setStateCode(settings.data.tax?.stateCode ?? '');
    setGstin(settings.data.tax?.gstin ?? '');
  }, [settings.data]);

  const save = useMutation({
    mutationFn: async () => {
      const updated = await api.patch<OrgSettings>('/settings', {
        tax: gstEnabled
          ? {
              regime: 'gst',
              stateCode,
              gstin: gstin || undefined,
              composition: settings.data?.tax?.composition ?? false,
              reverseChargeEnabled: settings.data?.tax?.reverseChargeEnabled ?? false,
              defaultTaxMode: settings.data?.tax?.defaultTaxMode ?? 'exclusive',
            }
          : { regime: 'none', composition: false, reverseChargeEnabled: false, defaultTaxMode: 'exclusive' },
      });
      // Seeding is idempotent by name, so turning GST on twice does not give
      // the picker ten rates.
      if (gstEnabled) await api.post('/taxes/seed-gst');
      return updated;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['settings', org?.id] });
      void queryClient.invalidateQueries({ queryKey: ['taxes', org?.id] });
    },
  });

  const error = save.error instanceof ApiError ? save.error : undefined;
  const canManage = perms.can('settings', 'manage');

  return (
    <>
      <PageHeader title="Settings" subtitle={org?.name} />

      <div className="max-w-2xl space-y-4 px-4 pb-10 sm:px-5">
        <Card>
          <CardHeader>
            <CardTitle>Your account</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <p>{user?.name}</p>
            <p className="text-muted-foreground">{user?.email}</p>
            <p className="text-muted-foreground">
              {org?.roleName} in {org?.name}
            </p>
            <Button variant="outline" className="mt-2" asChild>
              <Link to="/settings/account">Download or delete your account</Link>
            </Button>
          </CardContent>
        </Card>

        {/*
          Appearance. A per-DEVICE preference, so it sits with the account
          rather than under the business and is stored in this browser - a
          bright office monitor and a phone in the evening want different
          answers, and syncing it to the account would fight that. The same
          choice is one tap away in the sidebar; this is where it is explained.
        */}
        <Card>
          <CardHeader>
            <CardTitle>Appearance</CardTitle>
          </CardHeader>
          <CardContent>
            {/* A radio group rather than a select: two options with nothing
                hidden behind a dropdown, and each one announces its own
                state. Native radios, so the arrow keys already work. */}
            <fieldset>
              <legend className="mb-2 text-sm font-medium">Theme</legend>
              <div className="flex flex-wrap gap-2">
                {THEME_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className={cn(
                      'flex min-h-11 cursor-pointer items-center gap-2 rounded-md border px-3 text-sm sm:min-h-9',
                      'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2 has-[:focus-visible]:ring-offset-background',
                      themePref === option.value
                        ? 'border-primary bg-primary/10 font-medium text-primary'
                        : 'border-input text-foreground hover:bg-accent',
                    )}
                  >
                    <input
                      type="radio"
                      name="theme"
                      value={option.value}
                      checked={themePref === option.value}
                      onChange={() => setThemePref(option.value)}
                      className="sr-only"
                    />
                    <option.icon className="size-4" aria-hidden />
                    {option.label}
                    {/* The check is the non-colour signal: the selected option
                        differs from the other by more than its tint. */}
                    {themePref === option.value ? <Check className="size-4" aria-hidden /> : null}
                  </label>
                ))}
              </div>
            </fieldset>
            <p className="mt-2 text-xs text-muted-foreground">
              Remembered on this browser. Invoices print on white either way.
            </p>
          </CardContent>
        </Card>

        {/* Beside Appearance because it is the other per-device thing: an
            install is of this phone or this laptop, not of the account. */}
        <InstallCard />

        <Card>
          <CardHeader>
            <CardTitle>
              <Percent className="size-4 text-muted-foreground" aria-hidden />
              Tax
            </CardTitle>
          </CardHeader>
          {settings.isError ? (
            <ErrorState error={settings.error} onRetry={() => settings.refetch()} />
          ) : settings.isLoading ? (
            <SkeletonTable rows={3} cols={2} />
          ) : (
            <CardContent className="space-y-4">
              <div className="flex items-start gap-2 text-sm">
                <input
                  id="gst-enabled"
                  type="checkbox"
                  checked={gstEnabled}
                  disabled={!canManage}
                  onChange={(e) => setGstEnabled(e.target.checked)}
                  aria-describedby="gst-enabled-hint"
                  className="mt-0.5"
                />
                <div>
                  <label htmlFor="gst-enabled">Charge Indian GST</label>
                  <p id="gst-enabled-hint" className="text-xs text-muted-foreground">
                    Adds GSTIN, place of supply and HSN/SAC, and splits every rate into CGST + SGST
                    within your state or IGST outside it.
                  </p>
                </div>
              </div>

              {gstEnabled ? (
                <div className="grid gap-4 [&>*]:min-w-0 sm:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <span className="flex items-center text-sm font-medium">
                      <label htmlFor="gst-state">Registered state</label>
                      <span aria-hidden="true" className="ml-0.5 text-destructive">*</span>
                    </span>
                    <select
                      id="gst-state"
                      value={stateCode}
                      disabled={!canManage}
                      onChange={(e) => setStateCode(e.target.value)}
                      className="h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm"
                    >
                      <option value="">Select a state…</option>
                      {INDIAN_STATES.map((state) => (
                        <option key={state.code} value={state.code}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                    {error?.fieldError('tax.stateCode') ? (
                      <p className="text-xs text-destructive">{error.fieldError('tax.stateCode')}</p>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        This decides whether a sale is intra-state or inter-state.
                      </p>
                    )}
                  </div>

                  <Input
                    label="GSTIN"
                    value={gstin}
                    disabled={!canManage}
                    onChange={(e) => setGstin(e.target.value.toUpperCase())}
                    maxLength={15}
                    className="font-mono"
                    error={error?.fieldError('tax.gstin')}
                    hint="Checked against its checksum before it reaches an invoice."
                  />
                </div>
              ) : null}

              {error && !error.errors?.length ? (
                <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error.message}
                </p>
              ) : null}

              {canManage ? (
                <div className="flex items-center gap-3">
                  <Button loading={save.isPending} onClick={() => save.mutate()}>
                    Save tax settings
                  </Button>
                  {save.isSuccess && !save.isPending ? (
                    <span className="flex items-center gap-1 text-sm text-success">
                      <Check className="size-4" aria-hidden />
                      Saved
                    </span>
                  ) : null}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">Only an administrator can change these.</p>
              )}
            </CardContent>
          )}
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Tax rates</CardTitle>
          </CardHeader>
          <CardContent>
            {taxes.data?.items.length ? (
              <div className="flex flex-wrap gap-2">
                {taxes.data.items.map((tax) => (
                  <Badge key={tax.id} variant="outline">
                    {tax.name}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No rates yet. Turning GST on adds the five Indian rates.
              </p>
            )}
          </CardContent>
        </Card>

        {/*
          Under the rates, because it is the other half of what an export
          is charged: no LUT for the year and an export without IGST is a
          supply short-taxed. Only for a GST book - a book outside GST has
          no IGST to go without. Reads the SAVED regime, not the checkbox
          above, so ticking GST on does not offer a card that cannot save
          until the tax settings do.
        */}
        {settings.data?.tax?.regime === 'gst' ? <LutsCard /> : null}

        <Card>
          <CardHeader>
            <CardTitle>Book defaults</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row label="Base currency" value={settings.data?.baseCurrency ?? '—'} />
            <Row label="Timezone" value={settings.data?.timezone ?? '—'} />
            <Row
              label="Financial year starts"
              value={
                settings.data
                  ? new Date(2000, settings.data.fiscalYearStartMonth - 1, 1).toLocaleString('en', { month: 'long' })
                  : '—'
              }
            />
          </CardContent>
        </Card>

        {/*
          Under the book's defaults and before its colour: the letterhead is
          the book as a customer sees it, and the colour picked here is the
          accent the document falls back to.
        */}
        <LetterheadCard />

        <BookColourCard canManage={canManage} />

        {/* The shelf the book stands on - every member may see which; the owner rearranges. */}
        <OrganisationSettingsCard />

        {perms.can('member', 'view') ? (
          <Card>
            <CardHeader>
              <CardTitle>Members and roles</CardTitle>
            </CardHeader>
            <CardContent className="flex items-center justify-between gap-4 text-sm">
              <p className="text-muted-foreground">
                Who is in {org?.name ?? 'this book'}, what they can see, and who can
                change it.
              </p>
              <Button variant="outline" asChild>
                <Link to="/settings/members">Manage members</Link>
              </Button>
            </CardContent>
          </Card>
        ) : null}

        {/*
          Categories before tags, and that ordering is the argument this whole
          release rests on: the category is what the dashboard breaks money
          down by, and the tag is the label that cuts across it. A user who
          reads these in order learns which one to reach for.
        */}
        {/*
          Offered only to somebody who can read BOTH lists, because the endpoint
          asks for both permissions - a card whose only outcome is an error page
          is worse than a card that is not there.
        */}
        {perms.can('expense', 'view') && perms.can('invoice', 'view') ? (
        <Card>
          <CardHeader>
            <CardTitle>Categories</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-between gap-4 text-sm">
            <p className="text-muted-foreground">
              What kind of money it was - on the way in and on the way out. This is what your dashboard
              breaks your money down by.
            </p>
            {/*
              Named for what it manages, not just "Manage". Three cards whose
              only control is a link called "Manage" is three controls with the
              same accessible name, which a screen reader reads as three
              identical choices - and which a Playwright locator cannot tell
              apart either. The ambiguity is the app's, not the test's.
            */}
            <Button variant="outline" asChild>
              <Link to="/settings/categories">Manage categories</Link>
            </Button>
          </CardContent>
        </Card>
        ) : null}

        {perms.can('tag', 'view') ? (
          <Card>
            <CardHeader>
              <CardTitle>Tags</CardTitle>
            </CardHeader>
            <CardContent className="flex items-center justify-between gap-4 text-sm">
              <p className="text-muted-foreground">
                Labels for the things that cut across your categories - "Diwali campaign",
                "Reimbursable". A record can carry several, so they filter rather than add up.
              </p>
              <Button variant="outline" asChild>
                <Link to="/settings/tags">Manage tags</Link>
              </Button>
            </CardContent>
          </Card>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle>Subscriptions</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-between gap-4 text-sm">
            <p className="text-muted-foreground">
              Invoices that repeat - a hosting re-charge, a retainer, a domain renewal. Each one raises a
              draft when it falls due; nothing is issued or emailed until you say so.
            </p>
            <Button variant="outline" asChild>
              <Link to="/recurring">Manage subscriptions</Link>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Goals</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-between gap-4 text-sm">
            <p className="text-muted-foreground">
              A monthly target for money in, so the dashboard can tell you whether you are ahead or
              behind.
            </p>
            <Button variant="outline" asChild>
              <Link to="/settings/goals">Manage goals</Link>
            </Button>
          </CardContent>
        </Card>

        <PeriodLocksCard />
        {/*
          Connections BEFORE keys, and that ordering is the argument. Connecting
          an assistant is now the thing most people actually want, it needs no
          administrator, and it is the one a member can undo themselves. A key
          is the older, admin-only path for a client you configure by hand.
        */}
        <ConnectionsCard />
        <ApiKeysCard />
        <ToursCard />
        <VersionCard />
      </div>
    </>
  );
}

/**
 * The book's colour: the dot beside its name in the switcher, on the
 * Overview and on the Inbox's book buttons.
 *
 * Saved on the pick, not behind a button - one tap, one fact, and the
 * swatch's own check mark is the confirmation. The switcher updates from
 * the refreshed profile, so the person sees the change where it matters
 * without leaving the page.
 */
function BookColourCard({ canManage }: { canManage: boolean }) {
  const org = useActiveOrg();
  const queryClient = useQueryClient();
  const colour = resolveColour(org?.colour);

  const save = useMutation({
    mutationFn: (next: CategoryColour) => api.patch('/settings/book', { colour: next }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });

  return (
    <Card data-testid="book-colour-card">
      <CardHeader>
        <CardTitle>
          <ColourDot colour={colour} className="h-2.5 w-2.5" />
          Book colour
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-muted-foreground">
          How {org?.name ?? 'this book'} is marked in the switcher, on the Overview and in the Inbox, so two
          books are told apart at a glance.
        </p>
        {canManage ? (
          <ColourPicker label="Book colour" value={colour} onChange={(next) => save.mutate(next)} />
        ) : (
          <p className="text-muted-foreground">An administrator can change it.</p>
        )}
        {save.error ? (
          <p role="alert" className="text-destructive">
            {save.error instanceof ApiError ? save.error.message : 'Something went wrong'}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

const THEME_OPTIONS: Array<{ value: ThemePref; label: string; icon: typeof Sun }> = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
];

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
